/**
 * Light, dark, or follow the system. Stored per browser; applied as
 * `data-theme` on <html> so styles.css can override the media query.
 */
import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';

const KEY = 'field_iq_theme';

function read(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);
}

/** Resolve what is actually on screen, for the toggle's icon. */
export function resolvedTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme(): [ThemeMode, (m: ThemeMode) => void, 'light' | 'dark'] {
  const [mode, setMode] = useState<ThemeMode>(read);
  useEffect(() => {
    applyTheme(mode);
  }, [mode]);
  const set = useCallback((m: ThemeMode) => {
    try {
      if (m === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, m);
    } catch {
      /* private mode: the choice lasts for the tab */
    }
    setMode(m);
  }, []);
  return [mode, set, resolvedTheme(mode)];
}
